import { translateSubscriptionFeature, useTranslation } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { BillingCycle } from "@next-platform/client-common-core/user/types";
import { Modal } from "@next-platform/client-common-spicy-ui";
import { Button, RadioGroup, Tab, Tabs } from "@nextui-org/react";
import { Key, useEffect, useState } from "react";

import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { IPlanData } from "@/types";
import { getCurrencySymbol } from "@/utils";

import { ModalChangePlanProps, SubscriptionPlan } from "../types";
import PlanRadio from "./PlanRadio";

const ModalChangePlan = ({
  isOpen,
  onOpenChange,
  onChangePlan,
  currentPlanId,
  availablePlans,
  isActioning,
  currentBillingCycle,
  purchaseOptions,
}: ModalChangePlanProps) => {
  const { t } = useTranslation();
  const [selectedPlanId, setSelectedPlanId] = useState(currentPlanId);
  const [noteType, setNoteType] = useState("");
  const [selectedFrequency, setSelectedFrequency] = useState<BillingCycle>(currentBillingCycle);
  const isFrequencyLocked = currentBillingCycle === BillingCycle.ANNUAL;
  const isMonthlyTabVisible = currentBillingCycle === BillingCycle.MONTHLY;
  const isQuarterlyPlanAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.BillingQuarterlyPlan);
  const isQuarterlyTabVisible =
    currentBillingCycle !== BillingCycle.ANNUAL && Boolean(purchaseOptions?.quarterly_plans?.length) && isQuarterlyPlanAllowed;
  const effectiveFrequency = !isQuarterlyTabVisible && selectedFrequency === BillingCycle.QUARTERLY ? BillingCycle.MONTHLY : selectedFrequency;

  const plans = availablePlans?.filter((plan: SubscriptionPlan) => plan.billing_frequency === effectiveFrequency);
  const plansByCycle: Record<BillingCycle, IPlanData[] | undefined> = {
    [BillingCycle.MONTHLY]: purchaseOptions?.monthly_plans,
    [BillingCycle.QUARTERLY]: isQuarterlyPlanAllowed ? purchaseOptions?.quarterly_plans : undefined,
    [BillingCycle.ANNUAL]: purchaseOptions?.annual_plans,
  };

  useEffect(() => {
    setSelectedPlanId(currentPlanId);
  }, [currentPlanId]);

  useEffect(() => {
    setSelectedFrequency(currentBillingCycle);
  }, [currentBillingCycle]);

  useEffect(() => {
    if (selectedPlanId === currentPlanId) {
      setNoteType("");
      return;
    }
    if (plans) {
      const plan = plans?.find((plan: SubscriptionPlan) => plan.plan_id === selectedPlanId);

      if (plan && plan.upgrade_price > 0) {
        setNoteType("DISPLAY_PRICE");
      } else {
        setNoteType("NEXT_CYCLE");
      }
    }
  }, [selectedPlanId, currentPlanId, plans]);

  const upgradePrice = (plans || [])?.find((plan: SubscriptionPlan) => plan.plan_id === selectedPlanId)?.upgrade_price;

  const currency = (plans || [])?.find((plan: SubscriptionPlan) => plan.plan_id === selectedPlanId)?.currency;

  return (
    <Modal
      title={t("subscription:portal.modalChangePlan.title")}
      classNames={{ title: "text-center pl-[28px]" }}
      description={t("subscription:portal.modalChangePlan.description")}
      open={isOpen}
      onOpenChange={onOpenChange}
      backdrop="blur"
    >
      {!isFrequencyLocked ? (
        <Tabs size="sm" variant="bordered" selectedKey={effectiveFrequency} onSelectionChange={(key) => setSelectedFrequency(key as BillingCycle)}>
          <Tab key={BillingCycle.ANNUAL} title={<Trans i18nKey="subscription:portal.modalChangePlan.annualPlans" />} />
          {isQuarterlyTabVisible ? (
            <Tab
              key={BillingCycle.QUARTERLY}
              data-testid="ModalChangePlan-QuarterlyTab"
              title={<Trans i18nKey="subscription:portal.modalChangePlan.quarterlyPlans" />}
            />
          ) : null}
          {isMonthlyTabVisible ? (
            <Tab
              key={BillingCycle.MONTHLY}
              data-testid="ModalChangePlan-MonthlyTab"
              title={<Trans i18nKey="subscription:portal.modalChangePlan.monthlyPlans" />}
            />
          ) : null}
        </Tabs>
      ) : null}

      <RadioGroup aria-label="Plans" classNames={{ wrapper: "gap-3" }} value={selectedPlanId} onValueChange={setSelectedPlanId}>
        {plans?.map((plan: SubscriptionPlan, idx: Key) => {
          const frequencyPlans = plansByCycle[effectiveFrequency] ?? [];
          const matchedPurchaseOption = frequencyPlans?.find((option) => option.role === plan?.role);
          const benefits = (matchedPurchaseOption?.features ?? []).map((feature) => translateSubscriptionFeature({ feature, t }));

          const planLabel = plan?.name;
          return (
            <PlanRadio
              key={idx}
              isDisabled={plan?.plan_id === currentPlanId}
              benefits={benefits}
              label={planLabel}
              price={plan.price}
              currency={plan.currency}
              billing_frequency={plan?.billing_frequency}
              value={plan?.plan_id}
              className="p-2"
            />
          );
        })}
      </RadioGroup>
      <Col className="sticky bottom-0 bg-gray-3">
        {noteType === "NEXT_CYCLE" && (
          <p className="text-tiny  text-warning">
            <Trans i18nKey="subscription:portal.modalChangePlan.nextCycleNote" />
          </p>
        )}
        {noteType === "DISPLAY_PRICE" && (
          <p className="text-tiny  text-warning">
            <Trans
              i18nKey="subscription:portal.modalChangePlan.proratedNote"
              values={{ amount: `${getCurrencySymbol(currency)}${upgradePrice && parseFloat(String(upgradePrice)).toFixed(2)}` }}
            />
          </p>
        )}
        <Row justify="end" className="sticky bottom-0 bg-gray-3">
          <Button
            isDisabled={isActioning || selectedPlanId === currentPlanId}
            isLoading={isActioning}
            color="primary"
            onPress={() => onChangePlan(selectedPlanId)}
            aria-label="change-plan"
          >
            {isActioning ? (
              <span>
                <Trans i18nKey="subscription:portal.modalChangePlan.changingPlan" />
              </span>
            ) : (
              <span>
                <Trans i18nKey="subscription:portal.modalChangePlan.changePlan" />
              </span>
            )}
          </Button>
        </Row>
      </Col>
    </Modal>
  );
};

export default ModalChangePlan;
